/**
 * Direct MCP client: the VeloStruct page connects to the local MCP WebSocket
 * itself so Cursor tools work even when the Chrome extension is missing or
 * failing to detect the tab.
 *
 * Protocol matches the extension bridge: MCP sends {id, method, params},
 * the page calls window.__velostruct__[method] and replies {id, result|error}.
 */
import { useEffect } from 'react';

const PORTS = [7823, 7824, 7825, 7826];
const LOG = (...a: unknown[]) => console.info('[velostruct:mcp-direct]', ...a);

const ALLOWED_BRIDGE_METHODS = new Set([
  'getSummary', 'getNodes', 'findNode', 'getEdges',
  'addNode', 'updateNode', 'removeNode',
  'connectNodes', 'removeEdge', 'updateEdge',
  'addParticipant', 'removeParticipant', 'getParticipants',
  'addMessage', 'updateMessage', 'removeMessage', 'getMessages', 'insertMessage',
  'addFragment', 'removeFragment', 'getFragments', 'updateFragment',
  'importFromMermaid', 'exportToMermaid',
  'setPageDescription', 'getPageDetail',
  'autoLayoutFlow', 'reflowMessages', 'recalcFragments',
  'listPages', 'addPage', 'switchPage', 'linkNodeToPage',
  'frameNodes', 'createGroup', 'dissolveGroup',
  'clearDiagram', 'setDiagramType', 'getDiagramJson', 'setDiagramJson',
  'getAIContext', 'exportToPng', 'applyBatch',
  'updateNodeMeta', 'getCatalog', 'updateCatalog', 'placeCatalogInstance',
  'createChildView', 'createC4Workspace',
]);

function dispatchCall(method: string, params: unknown, isArray: boolean): unknown {
  const api = window.__velostruct__ as unknown as Record<string, (...args: unknown[]) => unknown> | undefined;
  if (!api) throw new Error('Bridge not available — window.__velostruct__ is undefined');
  if (!ALLOWED_BRIDGE_METHODS.has(method) || typeof api[method] !== 'function') {
    throw new Error(`Unknown method: ${method}`);
  }
  const fn = api[method];
  if (isArray && Array.isArray(params)) return fn(...params);
  if (params !== undefined && params !== null) return fn(params);
  return fn();
}

function handleMessage(ws: WebSocket, event: MessageEvent) {
  let msg: { type?: string; id?: string; method?: string; params?: unknown; isArray?: boolean };
  try { msg = JSON.parse(String(event.data)); } catch { return; }

  if (msg.type === 'ping') {
    ws.send(JSON.stringify({ type: 'pong' }));
    return;
  }
  if (msg.type === 'pong' || msg.type === 'status') return;
  if (!msg.id || !msg.method) return;

  const reply = (payload: Record<string, unknown>) => {
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ id: msg.id, ...payload }));
  };

  try {
    const result = dispatchCall(msg.method, msg.params, msg.isArray === true);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<unknown>)
        .then(r => reply({ result: r }))
        .catch(e => reply({ error: e instanceof Error ? e.message : String(e) }));
    } else {
      reply({ result });
    }
  } catch (e) {
    reply({ error: e instanceof Error ? e.message : String(e) });
  }
}

async function tryConnectPort(port: number, signal: AbortSignal): Promise<WebSocket> {
  const res = await fetch(`http://127.0.0.1:${port}/bridge-token`, { signal });
  if (!res.ok) throw new Error(`token HTTP ${res.status}`);
  const token = (await res.text()).trim();
  if (!token) throw new Error('empty token');

  return new Promise((resolve, reject) => {
    const ws = new WebSocket(`ws://127.0.0.1:${port}/?token=${encodeURIComponent(token)}`);
    const onOpen = () => {
      cleanup();
      resolve(ws);
    };
    const onError = () => {
      cleanup();
      try { ws.close(); } catch { /* ignore */ }
      reject(new Error(`ws failed on ${port}`));
    };
    const onAbort = () => {
      cleanup();
      try { ws.close(); } catch { /* ignore */ }
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const cleanup = () => {
      ws.removeEventListener('open', onOpen);
      ws.removeEventListener('error', onError);
      signal.removeEventListener('abort', onAbort);
    };
    ws.addEventListener('open', onOpen);
    ws.addEventListener('error', onError);
    signal.addEventListener('abort', onAbort);
  });
}

export function useMcpDirectClient(): void {
  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const connect = async () => {
      if (cancelled) return;

      for (const port of PORTS) {
        if (cancelled) break;
        try {
          const socket = await tryConnectPort(port, AbortSignal.timeout(1500));
          if (cancelled) {
            socket.close();
            return;
          }
          ws = socket;
          attempt = 0;
          LOG(`connected on port ${port}`);
          socket.addEventListener('message', (ev) => handleMessage(socket, ev));
          socket.addEventListener('close', () => {
            if (ws === socket) ws = null;
            if (!cancelled) scheduleRetry();
          });
          return;
        } catch {
          // try next port
        }
      }

      if (!cancelled) scheduleRetry();
    };

    const scheduleRetry = () => {
      attempt += 1;
      const delay = Math.min(4000 + attempt * 2000, 15000);
      retryTimer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, []);
}
